import React, {
  createContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import apiClient from "../auth/apiClient";
import NotesService from "../services/NotesService";
import notesReducer, {
  SET_NOTES,
  ADD_NOTE,
  UPDATE_NOTE,
  TOGGLE_PIN,
  DELETE_NOTE,
  SET_LOADING,
  SET_ERROR,
} from "../reducers/notesReducer";
import { useEnqueueSnackbar } from "../hooks/useEnqueueSnackbar";
import { NoteType } from "../constants/NoteCategoryEnum";

export const NotesContext = createContext();

const notesService = new NotesService(apiClient);

const initialState = {
  notes: [],
  isLoading: false,
  error: null,
};

const NotesProvider = ({ noteTypeId, contextId, entityId, children }) => {
  const [state, dispatch] = useReducer(notesReducer, initialState);
  const enqueueSnackbar = useEnqueueSnackbar();
  const currentUserId = useSelector((state) => state.user?.UserID);

  const refetchNotes = useCallback(async () => {
    try {
      const response = await notesService.getNotes(noteTypeId, contextId, null);
      dispatch({ type: SET_NOTES, payload: response.data });
    } catch {
      // silent — optimistic state is still valid
    }
  }, [noteTypeId, contextId]);

  useEffect(() => {
    let cancelled = false;

    const fetchNotes = async () => {
      dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await notesService.getNotes(noteTypeId, contextId, null);
        if (!cancelled) {
          dispatch({ type: SET_NOTES, payload: response.data });
        }
      } catch {
        if (!cancelled) {
          dispatch({ type: SET_ERROR, payload: "Failed to load notes" });
          enqueueSnackbar("Failed to load notes", {
            variant: "error",
            tag: "notesLoadError",
          });
        }
      } finally {
        if (!cancelled) {
          dispatch({ type: SET_LOADING, payload: false });
        }
      }
    };

    fetchNotes();
    return () => { cancelled = true; };
  }, [noteTypeId, contextId]);

  const addNote = useCallback(async (content, pinned = false) => {
    try {
      const response = await notesService.createNote({
        noteText: content,
        noteTypeID: noteTypeId,
        contextID: contextId,
        pinned,
        userID: currentUserId,
        entityID: entityId,
      });
      dispatch({ type: ADD_NOTE, payload: response.data });
      await refetchNotes();
    } catch {
      dispatch({ type: SET_ERROR, payload: "Failed to add note" });
      enqueueSnackbar("Failed to add note", {
        variant: "error",
        tag: "notesAddError",
      });
      throw new Error("Failed to add note");
    }
  }, [noteTypeId, contextId, entityId, currentUserId, enqueueSnackbar, refetchNotes]);

  const editNote = useCallback(async (noteID, content) => {
    const previousNotes = state.notes;
    const note = state.notes.find((n) => n.noteID === noteID);
    dispatch({
      type: UPDATE_NOTE,
      payload: { noteID, noteText: content },
    });
    try {
      await notesService.updateNote({
        noteID,
        noteText: content,
        pinned: note?.pinned,
        userID: currentUserId,
        entityID: entityId,
      });
      await refetchNotes();
    } catch {
      dispatch({ type: SET_NOTES, payload: previousNotes });
      enqueueSnackbar("Failed to save note", {
        variant: "error",
        tag: "notesEditError",
      });
    }
  }, [state.notes, entityId, currentUserId, enqueueSnackbar, refetchNotes]);

  const togglePin = useCallback(async (noteID) => {
    const previousNotes = state.notes;
    const note = state.notes.find((n) => n.noteID === noteID);
    dispatch({ type: TOGGLE_PIN, payload: { noteID } });
    try {
      await notesService.updateNote({
        noteID,
        noteText: note?.noteText,
        pinned: !note?.pinned,
        userID: currentUserId,
        entityID: entityId,
      });
      await refetchNotes();
    } catch {
      dispatch({ type: SET_NOTES, payload: previousNotes });
      enqueueSnackbar("Failed to update note", {
        variant: "error",
        tag: "notesPinError",
      });
    }
  }, [state.notes, entityId, currentUserId, enqueueSnackbar, refetchNotes]);

  const deleteNote = useCallback(async (noteID) => {
    const previousNotes = state.notes;
    dispatch({ type: DELETE_NOTE, payload: { noteID } });
    try {
      await notesService.deleteNote({
        noteID,
        userID: currentUserId,
        entityID: entityId,
      });
      await refetchNotes();
    } catch {
      dispatch({ type: SET_NOTES, payload: previousNotes });
      enqueueSnackbar("Failed to delete note", {
        variant: "error",
        tag: "notesDeleteError",
      });
    }
  }, [state.notes, entityId, currentUserId, enqueueSnackbar, refetchNotes]);

  const sortedNotes = useMemo(() => {
    return [...state.notes].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      const dateA = new Date(a.updatedDate || a.createdDate || 0);
      const dateB = new Date(b.updatedDate || b.createdDate || 0);
      return dateB - dateA;
    });
  }, [state.notes]);

  const value = useMemo(
    () => ({
      notes: sortedNotes,
      isLoading: state.isLoading,
      error: state.error,
      addNote,
      editNote,
      togglePin,
      deleteNote,
    }),
    [sortedNotes, state.isLoading, state.error, addNote, editNote, togglePin, deleteNote]
  );

  return (
    <NotesContext.Provider value={value}>
      {children}
    </NotesContext.Provider>
  );
};

export default NotesProvider;
